import { useState } from 'react';
import { Modal, Stack, Text, Table, Group, Badge, Button, Divider } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { CheckCircle } from 'react-feather';
import { cohortCompensationApi } from '../../../../services/api/cohortCompensation';
import type { CoachCompensationResult } from '../../../../types';
import { formatCents } from '../../../../utils/formatters';

interface BulkConfirmModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  coaches: CoachCompensationResult[];
  onConfirmed: () => void;
}

export function BulkConfirmModal({
  opened,
  onClose,
  cohortId,
  coaches,
  onConfirmed,
}: BulkConfirmModalProps) {
  const [loading, setLoading] = useState(false);

  const systemCalculatedCoaches = coaches.filter(c => c.status === 'system_calculated');
  const totalAmount = systemCalculatedCoaches.reduce(
    (sum, c) => sum + (c.estimated_total_cents ?? 0),
    0,
  );

  const handleConfirmAll = async () => {
    setLoading(true);
    try {
      const resultIds = systemCalculatedCoaches.map(c => c.id);
      await cohortCompensationApi.bulkConfirmResults(cohortId, resultIds);
      notifications.show({
        title: 'All Compensation Confirmed',
        message: `${systemCalculatedCoaches.length} coach compensation records confirmed`,
        color: 'green',
      });
      onConfirmed();
      onClose();
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to bulk confirm compensation',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Confirm All Compensation"
      size="lg"
      styles={{ title: { fontWeight: 700, fontSize: 16 } }}
    >
      <Stack gap="md">
        <Text size="sm">
          This will confirm {systemCalculatedCoaches.length} coach compensation record
          {systemCalculatedCoaches.length !== 1 ? 's' : ''} for payment scheduling.
        </Text>

        <Table
          withTableBorder
          striped
          styles={{
            th: { fontSize: 11, fontWeight: 600, textTransform: 'uppercase' },
          }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Coach</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Estimated Total</Table.Th>
              <Table.Th style={{ textAlign: 'center' }}>Groups</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {systemCalculatedCoaches.map(coach => {
              const hasAdjustments = coach.line_items?.some(
                li => li.adjusted_total_cents !== null,
              );
              return (
                <Table.Tr key={coach.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {coach.coach_name}
                    </Text>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <Group gap={4} justify="flex-end">
                      <Text size="sm" fw={600}>
                        {formatCents(coach.estimated_total_cents ?? 0)}
                      </Text>
                      {hasAdjustments && (
                        <Badge size="xs" color="orange" variant="light">
                          ADJUSTED
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'center' }}>
                    <Text size="sm">{coach.group_count}</Text>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>

        <Divider />

        <Group justify="space-between">
          <Text size="sm" fw={600}>
            Total Being Confirmed
          </Text>
          <Text size="lg" fw={700}>
            {formatCents(totalAmount)}
          </Text>
        </Group>

        <Group justify="flex-end" gap="sm">
          <Button variant="outline" color="gray" onClick={onClose}>
            Cancel
          </Button>
          <Button
            leftSection={<CheckCircle size={16} />}
            onClick={handleConfirmAll}
            loading={loading}
          >
            Confirm All ({systemCalculatedCoaches.length})
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
